import {
  fetchPageData,
  fetchTheme,
  fetchThemePreview,
  updateCustomisationSetting,
  fetchPreviewPageData,
  setThemeCache, setPageDataCache
} from './action'
import { createSlice } from '@reduxjs/toolkit'
import { safeDeepUpdate } from 'src/utils/object'
import { HYDRATE } from 'next-redux-wrapper'

interface CurrentAction {
  type: string
  payload: {
    pageId: string
    previewToken: string
    sectionId: string
    action: string
  }
}

interface CustomisationState {
  previewMode: boolean
  themeSettingData: Customisation.ThemeSettingData
  sectionSettingData: Customisation.SectionSettingData
  siteSettingData: Customisation.SiteSettingData
  sortingSectionOrder: string[]
  sortingSectionId: string
  currentAction: CurrentAction
  siteId: string
}

export type CustomisationStateInReducer = {
  '@customisation': PlainInfo<CustomisationState>
}

const initialState: PlainInfo<CustomisationState> = {
  status: 'none',
  data: {
    siteId: '',
    themeSettingData: {
      selectedPreset: '',
      settings: {
        colorSectionHeadingsFont: '',
        colorHeadingsFont: '',
        colorBodyFont: '',
        colorLinesAndBorders: '',
        colorContainerColor: '',
        colorBackgroundColor: '',
        typographyHeadings: '',
        typographyBodyText: '',
        buttonStyle: '',
        buttonShape: '',
        buttonTextColor: '',
        buttonColor: ''
      }
    },
    sectionSettingData: {
      _id: '',
      sections: {},
      pages: {}
    },
    siteSettingData: {
      homePageMetaDescription: '',
      homePageTitle: '',
      siteKeywordsTag: ''
    },
    sortingSectionOrder: [],
    sortingSectionId: '',
    currentAction: {} as CurrentAction,
    previewMode: false
  }
}

type PlainInfo<T> = {
  status: RequestState
  error?: string
  data: T
}

type RequestState = 'pending' | 'fulfilled' | 'rejected' | 'none'

export const customisationSlice = createSlice({
  name: '@customisation',
  initialState,
  reducers: {},
  extraReducers: (builder) =>
    builder
      .addCase(fetchTheme.pending, (state) => {
        state.status = 'pending'
      })
      .addCase(fetchTheme.fulfilled, (state, action) => {
        state.status = 'fulfilled'
        state.data.siteId = action.payload.themeData.siteId
        state.data.themeSettingData = action.payload.themeData.themeSettingData
        state.data.siteSettingData = action.payload.themeData.siteSettingData
      })
      .addCase(fetchTheme.rejected, (state, action) => {
        state.status = 'rejected'
        state.error = action.error.message
      })
      .addCase(fetchPageData.pending, (state) => {
        state.status = 'pending'
      })
      .addCase(fetchPageData.fulfilled, (state, action) => {
        state.status = 'fulfilled'
        state.data.sectionSettingData._id = action.payload?._id || null
        state.data.sectionSettingData.pages = {
          ...state.data.sectionSettingData.pages,
          ...action.payload.pages
        }
        state.data.sectionSettingData.sections = {
          ...state.data.sectionSettingData.sections,
          ...action.payload.sections
        }
      })
      .addCase(fetchPageData.rejected, (state, action) => {
        state.status = 'rejected'
        state.error = action.error.message
      })
      .addCase(fetchThemePreview.pending, (state) => {
        state.status = 'pending'
        state.data.previewMode = true
      })
      .addCase(fetchThemePreview.fulfilled, (state, action) => {
        state.status = 'fulfilled'
        state.data.siteId = action.payload.themeData.siteId
        state.data.themeSettingData = action.payload.themeData.themeSettingData
        state.data.siteSettingData = action.payload.themeData.siteSettingData
      })
      .addCase(fetchThemePreview.rejected, (state, action) => {
        state.status = 'rejected'
        state.error = action.error.message
      })
      .addCase(fetchPreviewPageData.pending, (state) => {
        state.status = 'pending'
      })
      .addCase(fetchPreviewPageData.fulfilled, (state, action) => {
        state.status = 'fulfilled'
        state.data.sectionSettingData._id = action.payload?._id || null
        state.data.sectionSettingData.pages = {
          ...state.data.sectionSettingData.pages,
          ...action.payload.pages
        }
        state.data.sectionSettingData.sections = {
          ...state.data.sectionSettingData.sections,
          ...action.payload.sections
        }
      })
      .addCase(fetchPreviewPageData.rejected, (state, action) => {
        state.status = 'rejected'
        state.error = action.error.message
      })
      .addCase(updateCustomisationSetting, (state, action) => {
        const {
          themeSettingData,
          sectionSettingData,
          sortingSectionOrder,
          sortingSectionId,
          currentAction
        } = action.payload
        safeDeepUpdate(state.data, ['themeSettingData'], themeSettingData)
        safeDeepUpdate(state.data, ['sectionSettingData'], sectionSettingData)
        safeDeepUpdate(state.data, ['sortingSectionOrder'], sortingSectionOrder)
        safeDeepUpdate(state.data, ['sortingSectionId'], sortingSectionId)
        safeDeepUpdate(state.data, ['currentAction'], currentAction)
      })
      .addCase(setThemeCache, (state, action) => {
        state.status = action.payload.status
        state.data.siteId = action.payload.data.siteId
        state.data.themeSettingData = action.payload.data.themeSettingData
        state.data.siteSettingData = action.payload.data.siteSettingData
      })
      .addCase(setPageDataCache, (state, action) => {
        state.status = 'fulfilled'
        state.data.sectionSettingData._id = action.payload?._id || null
        state.data.sectionSettingData.pages =  action.payload?.pages || {}
        state.data.sectionSettingData.sections = action.payload?.sections || {}
      })
      .addCase(HYDRATE, (state, action) => ({
        ...state,
        ...action['payload']['@customisation']
      }))
})
